import { Component, ViewChild } from "@angular/core";
import { SlickDialogComponent, SlickToastService } from "@slick-components";
import { ICustomerModel } from "@models";
import { CustomersService, SleepService } from "@services";

// The old CRM tab from the customer page, on its own. Outreach is edited straight on the customer
// model and rides the ordinary customer save, so this dialog owns that save rather than leaving the
// caller to do it.
@Component({
	selector: "crm-edit-dialog",
	templateUrl: "crm-edit-dialog.component.html",
	providers: [SlickToastService, CustomersService]
})
export class CrmEditDialogComponent {
	@ViewChild("dialogRef", { static: true }) dialogRef: SlickDialogComponent;

	customerModel: ICustomerModel;
	spinnerStatus: string;

	private resolve: (customerModel: ICustomerModel) => void;

	constructor(private readonly customersService: CustomersService,
		private readonly slickToastService: SlickToastService) {
	}

	get dialogHeader(): string {
		const name = this.customerModel?.displayName || this.customerModel?.fullName;

		return name ? `CRM - ${name}` : 'CRM';
	}

	// Takes the id, not a model: outreach only comes back on the full customer, and the whole model is
	// posted on save, so handing this a partial row from a grid would blank whatever it was missing.
	// Resolves with the saved customer, or null if the dialog was cancelled.
	async openDialog(customerId: number): Promise<ICustomerModel> {
		this.spinnerStatus = "reset";
		this.customerModel = null;

		try {
			this.customerModel = await this.customersService.getCustomer(customerId);
		}
		catch {
			this.slickToastService.showDanger("Unable to load this customer.");
			return null;
		}

		this.dialogRef.showDialog();

		return new Promise<ICustomerModel>((resolve) => {
			this.resolve = resolve;
		});
	}

	async onSave() {
		try {
			this.spinnerStatus = "spin";
			this.customerModel = await this.customersService.updateCustomer(this.customerModel);
			this.spinnerStatus = "ok";
			this.slickToastService.showSuccess("CRM saved");

			await SleepService.sleep(500);

			this.dialogRef.hideDialog();
			this.resolve(this.customerModel);
		}
		catch {
			this.spinnerStatus = "error";
			this.slickToastService.showDanger("Unable to save. Please try again.");
		}
	}

	onCancel() {
		this.dialogRef.hideDialog();
		this.resolve(null);
	}
}
